import React, { useContext, useMemo } from 'react';
import { Button, ButtonProps, mergeClasses } from '@fluentui/react-components';
import { ChevronDown24Regular } from '@fluentui/react-icons';

import { I18nContext } from '@bingads-webui-react/i18n-context';

import { useAccordionContext } from '../contexts/accordion';
import { useAccordionButtonStyles } from './accordion-button-styles';

interface I18nModel {
  getString(key: string, model?: Record<string, string>): string;
}

interface AccordionButtonProps extends Omit<ButtonProps, 'icon' | 'onClick' | 'aria-label'> {
  title?: string;
}

export const AccordionButton: React.FC<React.PropsWithChildren<AccordionButtonProps>> = ({ className, title, ...props }) => {
  const i18n = useContext(I18nContext) as I18nModel;
  const classes = useAccordionButtonStyles();
  const accordionContext = useAccordionContext();

  if (!accordionContext) { return null; }

  const { open, onOpenChange } = accordionContext;
  const openLabel = title ? i18n.getString(_TL_('Expand {{title}} section'), { title }) : i18n.getString(_TL_('Expand section'));
  const closeLabel = title ? i18n.getString(_TL_('Collapse {{title}} section'), { title }) : i18n.getString(_TL_('Collapse section'));

  const label = useMemo(() => (open ? closeLabel : openLabel), [closeLabel, open, openLabel]);

  const onClickButton = (event: any) => {
    onOpenChange(!open);
    event.stopPropagation();
  };

  return (
    <Button
      className={mergeClasses('accordion-button', classes.root, className)}
      appearance="subtle"
      size="large"
      icon={<ChevronDown24Regular className={mergeClasses(classes.icon, open && classes.iconCollapsed)} />}
      onClick={onClickButton}
      aria-expanded={open}
      aria-label={label}
      /* istanbul ignore next */
      {...props as ButtonProps}
    />
  );
};
